/**
 * 文字の大きさの切替
 * 「標準 / 大きめ / とても大きい」を選ぶと、全画面の文字がすぐに大きくなり、端末に保存されます。
 * ログイン前の画面・各画面の「?」・ホームに同じ部品を置きます（保存先は1つ）。
 */

import React, { useId } from 'react';
import { Pressable, Text, View } from './WebPrimitives';
import useTextSizeLevel from '../hooks/useTextSizeLevel';
import { TEXT_SIZE_LEVELS, type TextSizeLevelId } from '../services/textSizePreference';

/** 各段階のボタンに出す見本「あ」の大きさ。段階を足して対応を書き忘れると型検査が失敗する */
const SAMPLE_CLASS_NAME_MAP: Record<TextSizeLevelId, string> = {
  standard: 'text-base',
  large: 'text-lg',
  xlarge: 'text-xl',
};

/**
 * 文字の大きさを選ぶラジオボタンを表示する
 * @returns {React.JSX.Element} 文字の大きさの切替
 */
const TextSizeControl = (): React.JSX.Element => {
  const [selectedLevelId, setLevel] = useTextSizeLevel();
  const headingId = useId();

  return (
    <View
      accessibilityRole="radiogroup"
      aria-labelledby={headingId}
      className="gap-2 border-2 border-app-border bg-app-card-soft p-3"
      testID="text-size-control"
    >
      <Text className="text-sm font-extrabold text-app-quest-night" id={headingId}>
        文字の大きさ
      </Text>
      <View className="flex-row gap-2">
        {TEXT_SIZE_LEVELS.map((level) => {
          const isSelected = level.id === selectedLevelId;
          return (
            <Pressable
              accessibilityRole="radio"
              accessibilityState={{ checked: isSelected }}
              className={`min-h-[48px] min-w-0 flex-1 items-center justify-center border-2 border-app-quest-night px-1 py-1 ${isSelected ? 'bg-app-quest-night' : 'bg-app-card'}`}
              key={level.id}
              onPress={() => setLevel(level.id)}
              testID={`text-size-option-${level.id}`}
            >
              <Text
                accessible={false}
                className={`${SAMPLE_CLASS_NAME_MAP[level.id]} font-extrabold ${isSelected ? 'text-white' : 'text-app-quest-night'}`}
              >
                あ
              </Text>
              <Text
                className={`text-center text-xs font-bold ${isSelected ? 'text-white' : 'text-app-quest-night'}`}
              >
                {level.label}
              </Text>
            </Pressable>
          );
        })}
      </View>
    </View>
  );
};

export default TextSizeControl;
